import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { propsFromFields } from 'components/helpers/Heading';
import { classnames } from 'tailwindcss-classnames';
import Button from 'components/helpers/Button';
import getThemeClasses from 'lib/util/theming';
import ImageWrapper from '../ImageWrapper/ImageWrapper';
import { ButtonVariant } from '../Button/WithButton';
import { OnLinkDataLayerPush } from 'lib/util/gtm-datalayer';
import { shouldSuppressLink } from '../LinkA11yWrapper';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import ImageCaption from '../ImageCaption/ImageCaption';
import { Field } from '@sitecore-jss/sitecore-jss-react';
import { useScreenBreakpoint } from 'lib/util/useScreenWidth';
import CardHeading from './cardHeading';

export type CalloutCardItem = Sitecore.Override.ItemEx &
  Components.General.CalloutCard.Fields.CalloutCard;

interface CalloutCardCardProps {
  promotedPage: CalloutCardItem;
  isSingleCard?: boolean;
  onGtmPush?: OnLinkDataLayerPush;
}

const CalloutCardCard = ({
  promotedPage,
  isSingleCard,
  onGtmPush,
}: CalloutCardCardProps): JSX.Element => {
  const { fields } = promotedPage;
  const isXpEditor = useExperienceEditor();
  const { isMobileScreen } = useScreenBreakpoint();
  const isMobileView = isMobileScreen;

  if (!fields) {
    return <></>;
  }
  // Extract cta link fields, determine if we are showing 1 or 2 of them
  const { cta1, cta2, imageCaption } = fields;
  const hasTwoCtas = !shouldSuppressLink({
    link: cta2,
    isExperienceEditor: isXpEditor,
  });
  const headingProps = propsFromFields(fields);

  const cta2TypeField = fields.cta2Type.value;
  const cta2Type: ButtonVariant =
    cta2TypeField === 'Default' || cta2TypeField.trim().length === 0
      ? 'secondary'
      : (cta2TypeField.toLowerCase() as ButtonVariant);
  const imageCaptionValue: Field<string> | undefined =
    imageCaption && typeof imageCaption === 'object' && 'value' in imageCaption
      ? imageCaption
      : undefined;

  return (
    <>
      <div className={classnames('callout-card-img')}>
        <ImageWrapper field={fields.image} layout="responsive" />
      </div>
      <div
        className={classnames('callout-card-content', 'max-h-full', getThemeClasses('theme-white'))}
      >
        <CardHeading
          headingProps={headingProps}
          imageCaption={imageCaption}
          isSingleCard={isSingleCard}
        />
        <div className={classnames('cta-container', 'pt-6')}>
          <Button
            linkField={cta1}
            onGtmPush={onGtmPush}
            className={classnames({ 'cta-left': hasTwoCtas })}
          />
          <Button
            linkField={cta2}
            variant={cta2Type}
            onGtmPush={onGtmPush}
            className={classnames({ 'cta-right': hasTwoCtas })}
          />
        </div>
        {isSingleCard && !isMobileView && <ImageCaption imageCaption={imageCaptionValue} />}
      </div>
    </>
  );
};

export default CalloutCardCard;
